import { connect } from 'react-redux';
import { safeFormatClientDate } from 'shared/util/datetime-fns';

import { actions } from '@/state/filterActions';
import { fetchHotels } from '@/ducks/searchResults/actions';
import type { AccommodationState, CheckboxMap, Filters, OverrideValues } from '@/interfaces';
import type { ActionCreator } from 'redux';
import { generateRoomInfoText } from '@/containers/travellersPicker/TravellersPicker';
import { nameOverrides } from '@/utils/nameOverrides';
import emptyStateIconUrl from '@/public/images/empty-state.svg';

interface StateProps {
    filters: Filters;
    destinationFullName: string;
}

interface DispatchProps {
    resetFilters: () => void;
    triggerFetch: () => void;
}

export type SearchResultsEmptyWarningProps = DispatchProps & StateProps;

const emptyCheckboxMap = (map: CheckboxMap): boolean => Object.keys(map).filter((key) => map[key]).length === 0;

export const emptyFilters = (filters: Filters): boolean =>
    (filters.page === 0 || filters.page === undefined) &&
    !filters.hotelName &&
    emptyCheckboxMap(filters.facility) &&
    emptyCheckboxMap(filters.hotelChain) &&
    emptyCheckboxMap(filters.guestRating) &&
    emptyCheckboxMap(filters.starRating) &&
    emptyCheckboxMap(filters.favoriteHotels) &&
    emptyCheckboxMap(filters.propertyType) &&
    filters.price === undefined;

export const SearchResultsEmptyWarning = ({ destinationFullName, filters, resetFilters, triggerFetch }: SearchResultsEmptyWarningProps) => {
    const badSearchWarning = (
        <>
            <p className="u-cherry u-strong" data-testid="searchDetails">
                {destinationFullName}, {generateRoomInfoText(filters.rooms)}, {safeFormatClientDate(filters.checkInDate)} -{' '}
                {safeFormatClientDate(filters.checkOutDate)}
            </p>
            <p data-testid="warningText">Hvis du velger en annen dato eller et annet sted kan vi finne et tilbud til deg.</p>
        </>
    );
    const badFiltersWarning = (
        <>
            <p data-testid="warningText">Hvis du endrer på kriteriene kan vi finne et tilbud til deg.</p>
            <button
                data-testid="emptyWarningButton"
                type="button"
                aria-label="Nullstill filtere"
                className="button button--default u-mv32"
                onClick={(): void => {
                    resetFilters();
                    triggerFetch();
                }}>
                Nullstill filtere
            </button>
        </>
    );
    return (
        <div className="u-text-center u-mt32" data-testid="emptyWarning">
            <p className="u-t2 u-mb16">Vi fant ingen overnattingstilbud på søket ditt</p>
            {emptyFilters(filters) ? badSearchWarning : badFiltersWarning}
            <div className="u-mt32">
                <img src={emptyStateIconUrl.src} className="img-format__img u-size1of2" alt="Katt som tar på en badeball" />
            </div>
        </div>
    );
};

const mapStateToProps = (state: AccommodationState): StateProps => {
    let destinationFullName =
        nameOverrides[state.filters.destination?.toLowerCase()] ||
        state.searchResults.destination?.fullName ||
        state.filters.destination?.replace(/_/g, ' ');
    if (state.filters.mapBounds) {
        destinationFullName = 'Kartutsnitt';
    } else if (!state.filters.destination && !state.filters.mapBounds) {
        destinationFullName = state.searchResults.searchConfig.title;
    }
    return { filters: state.filters, destinationFullName };
};

const mapDispatchToProps: OverrideValues<DispatchProps, ActionCreator<unknown>> = {
    resetFilters: actions.resetFilters,
    triggerFetch: fetchHotels,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsEmptyWarning);
